<template>
  <div :class="$style.wrapper">
    <BaseLoading v-if="isClanLoading && !clan" />
    <div
      v-if="!isClanLoading && !clan"
      :class="[
        'px-2 text-center',
        $style.centeredWrapper
      ]"
    >
      <font-awesome-icon
        icon="users"
        size="5x"
        class="mb-3"
      />
      <BaseTextH2>{{ $t('pick-a-name-for-your-new-pool') }}</BaseTextH2>
      <TheCreateOrJoinClan
        :button-text="$t('create-a-pool')"
        icon="plus"
        :placeholder="$t('enter-pool-name')"
        @onFormSubmit="handleCreateClan"
      />

      <p>{{ $t('or') }}</p>

      <BaseTextH3>{{ $t('write-the-league-password-provided-by-your-friend-colleagues') }}</BaseTextH3>
      <TheCreateOrJoinClan
        :button-text="$t('join-a-pool')"
        icon="sign-in-alt"
        :placeholder="$t('enter-pool-password')"
        @onFormSubmit="handleJoinClan"
      />

      <router-link
        :to="{ name: 'home' }"
        class="mt-4"
      >
        <BaseButton
          is-rounded
          variant="outline-orange"
          font-size="small"
        >
          <span class="px-2">
            <font-awesome-icon icon="chevron-left" />
            <span class="pl-2">{{ $t('go-back-to-the-game') }}</span>
          </span>
        </BaseButton>
      </router-link>
    </div>
    <div
      v-if="!isClanLoading && clan"
      class="d-flex flex-column flex-fill"
    >
      <b-container>
        <b-row>
          <b-col class="p-0">
            <div
              :class="[
                'd-flex align-items-center justify-content-center align-items-center text-center py-2 mb-3',
                $style.headerWrapper
              ]"
            >
              <font-awesome-icon
                icon="users"
                size="2x"
              />
              <div class="pl-3 text-center">
                <BaseTextH2 class="m-0">
                  {{ clan.name }}
                </BaseTextH2>
                <span :class="$style.memberCountText">
                  ({{ $tc('x-members', clan.users.length, { x: clan.users.length }) }})
                </span>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
      <transition-group
        tag="div"
        enter-active-class="fadeInDown"
        leave-active-class="fadeOutDown"
        :duration="{ enter: 1000, leave: 400 }"
        mode="out-in"
      >
        <template>
          <b-row
            v-for="(user,userIndex) in clan.users"
            :key="`user-clan-${user.id}`"
            :style="{ animationDelay: `${userIndex * 0.05}s` }"
          >
            <b-col
              :class="[
                user.isMe ? $style.isMyPosition : $style.positionWrapper
              ]"
            >
              <b-container>
                <b-row>
                  <b-col
                    :cols="6"
                  >
                    <div class="d-flex align-items-center py-1">
                      <BaseAvatar
                        :url="user.avatar.image_url"
                        :size="75"
                        is-lazy
                      />
                      <div class="px-2 d-flex flex-column">
                        <span :class="$style.userNameText">
                          <template v-if="user.isMe">{{ $t('you') }}</template>
                          <template v-else>{{ user.name }}</template>
                        </span>
                        <span>
                          Score: <span :class="$style.scoreText">{{ user.score }}</span>
                        </span>
                      </div>
                    </div>
                  </b-col>
                  <b-col
                    :cols="6"
                  >
                    <div class="d-flex align-items-center justify-content-end pt-4 pr-4">
                      <router-link :to="{ name: 'my-friends-picks-page', params: { userId: user.id }}">
                        <BaseButton
                          is-rounded
                          variant="outline-orange"
                          font-size="small"
                        >
                          <span class="px-2">
                            <span class="pr-2">{{ $t('view-picks') }}</span>
                            <font-awesome-icon icon="chevron-right" />
                          </span>
                        </BaseButton>
                      </router-link>
                    </div>
                  </b-col>
                </b-row>
              </b-container>
            </b-col>
          </b-row>
        </template>
      </transition-group>
      <b-container>
        <b-row
          :class="[
            'py-4 mt-3',
            $style.inviteWrapper
          ]"
        >
          <b-col
            :cols="12"
            class="text-center pb-4"
          >
            <BaseTextH2 class="text-uppercase">
              {{ $t('invite-your-friends-colleagues-to-join-pool-name', { poolName: clan.name }) }}
            </BaseTextH2>
            <div class="pt-3">
              {{ $t('send-them-this-link' ) }}
            </div>
            <div class="mb-3 d-flex flex-column align-items-center">
              <BaseTextH3>{{ inviteUrl }}</BaseTextH3>
              <BaseButton
                v-clipboard:copy="inviteUrl"
                v-clipboard:success="onSuccessCopy"
                class="d-flex mb-2"
                is-rounded
                variant="outline-green"
                font-size="small"
              >
                <span class="px-2">
                  <transition
                    enter-active-class="fadeInLeft"
                    leave-active-class="fadeOutLeft"
                    mode="out-in"
                    :duration="{ enter: 400, leave: 100 }"
                  >
                    <font-awesome-icon
                      v-if="isInviteUrlCopied"
                      key="copied"
                      icon="check"
                    />
                    <font-awesome-icon
                      v-if="!isInviteUrlCopied"
                      key="copy"
                      :icon="['far', 'copy']"
                    />
                  </transition>
                  <span :class="['pl-2', $style.copyText]">
                    <transition
                      enter-active-class="fadeInRight"
                      leave-active-class="fadeOutRight"
                      mode="out-in"
                      :duration="{ enter: 400, leave: 100 }"
                    >
                      <span
                        v-if="isInviteUrlCopied"
                        key="copied"
                      >
                        {{ $t('copied') }}
                      </span>
                      <span
                        v-if="!isInviteUrlCopied"
                        key="copy"
                      >
                        {{ $t('copy-the-link') }}
                      </span>
                    </transition>
                  </span>
                </span>
              </BaseButton>
            </div>

            <div>{{ $t('or-share-this-password' ) }}</div>
            <BaseTextH3 class="m-0 mb-2">
              {{ clan.password }}
            </BaseTextH3>
          </b-col>

          <b-col
            :cols="6"
            class="d-flex justify-content-end"
          >
            <BaseButton
              is-rounded
              :variant="isCreatePoolActive ? 'blue' : 'outline-blue'"
              font-size="small"
              @click="handleCreatePoolButton"
            >
              <span class="px-3 d-flex align-items-center">
                <font-awesome-icon icon="plus" />
                <span class="pl-2">{{ $t('create-a-pool') }}</span>
              </span>
            </BaseButton>
          </b-col>
          <b-col
            :cols="6"
            class="d-flex"
          >
            <BaseButton
              is-rounded
              :variant="isJoinPoolActive ? 'blue' : 'outline-blue'"
              font-size="small"
              @click="handleJoinPoolButton"
            >
              <span class="px-3 d-flex align-items-center">
                <font-awesome-icon icon="sign-in-alt" />
                <span class="pl-2">{{ $t('join-a-pool') }}</span>
              </span>
            </BaseButton>
          </b-col>
          <b-col>
            <slide-up-down :active="isCreatePoolActive">
              <div class="d-flex flex-column justify-content-center text-center py-3">
                <BaseTextH3>{{ $t('pick-a-name-for-your-new-pool') }}</BaseTextH3>
                <TheCreateOrJoinClan
                  :button-text="$t('create-a-pool')"
                  icon="plus"
                  :placeholder="$t('enter-pool-name')"
                  @onFormSubmit="handleCreateClan"
                />
              </div>
            </slide-up-down>
            <slide-up-down :active="isJoinPoolActive">
              <div class="d-flex flex-column justify-content-center text-center py-3">
                <BaseTextH3>{{ $t('write-the-league-password-provided-by-your-friend-colleagues') }}</BaseTextH3>
                <TheCreateOrJoinClan
                  :button-text="$t('join-a-pool')"
                  icon="sign-in-alt"
                  :placeholder="$t('enter-pool-password')"
                  @onFormSubmit="handleJoinClan"
                />
              </div>
            </slide-up-down>
            <slide-up-down :active="isCreatePoolActive || isJoinPoolActive">
              <div
                :class="[
                  'd-flex align-items-center justify-content-center',
                  $style.joinWarning
                ]"
              >
                <font-awesome-icon icon="exclamation-triangle" />
                <span class="pl-2">{{ $t('creating-or-joining-a-new-league-will-remove-you-from-the-current-league') }}</span>
              </div>
            </slide-up-down>
          </b-col>
        </b-row>
        <router-link
          :to="{ name: 'home' }"
          class="mt-4 d-flex justify-content-center"
        >
          <BaseButton
            is-rounded
            variant="outline-orange"
            font-size="small"
          >
            <span class="px-2">
              <font-awesome-icon icon="chevron-left" />
              <span class="pl-2">{{ $t('go-back-to-the-game') }}</span>
            </span>
          </BaseButton>
        </router-link>
      </b-container>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import VueClipboard from 'vue-clipboard2'
import SlideUpDown from 'vue-slide-up-down'
import { mapActions, mapState } from 'vuex'
import BaseAvatar from '@/components/Atoms/BaseAvatar'
import BaseLoading from '@/components/Atoms/BaseLoading'
import BaseButton from '@/components/Atoms/BaseButton'
import BaseTextH2 from '@/components/Atoms/Typography/BaseTextH2'
import BaseTextH3 from '@/components/Atoms/Typography/BaseTextH3'
import TheCreateOrJoinClan from '@/pages/Clan/Components/Molecules/TheCreateOrJoinClan'

Vue.use(VueClipboard)
VueClipboard.config.autoSetContainer = true
Vue.component('slide-up-down', SlideUpDown)

export default {
  name: 'ClanPage',
  components: {
    TheCreateOrJoinClan,
    BaseTextH3,
    BaseAvatar,
    BaseTextH2,
    BaseButton,
    BaseLoading,
  },
  metaInfo() {
    return {
      title: this.$t('page-titles.clan'),
      meta: [
        { name: 'description', content: this.$t('page-descriptions.clan') },
      ],
    }
  },
  data() {
    return {
      inviteUrl: false,
      isInviteUrlCopied: false,
      isCreatePoolActive: false,
      isJoinPoolActive: false,
      form: {
        join: {
          password: '',
        },
      },
    }
  },
  computed: {
    ...mapState({
      isClanLoading: state => state['profile/clan'].isClanLoading,
      clan: state => state['profile/clan'].clan,
    }),
  },
  watch: {
    clan(newValue) {
      this.inviteUrl = `${window.origin}/join/${newValue.password}`
    },
  },
  created() {
    this.fetchMyClan()
  },
  methods: {
    ...mapActions({
      fetchMyClan: 'profile/clan/fetchMyClan',
      joinClan: 'profile/clan/joinExistingClan',
      createClan: 'profile/clan/createClan',
    }),
    onSuccessCopy() {
      this.isInviteUrlCopied = true
      setTimeout(() => {
        this.isInviteUrlCopied = false
      }, 2500)
    },
    handleCreatePoolButton() {
      this.isCreatePoolActive = !this.isCreatePoolActive
      this.isJoinPoolActive = false
    },
    handleJoinPoolButton() {
      this.isJoinPoolActive = !this.isJoinPoolActive
      this.isCreatePoolActive = false
    },
    handleJoinClan(param) {
      this.joinClan({
        password: param,
      }).then((clan) => {
        if (clan) {
          this.fetchMyClan()
          this.isJoinPoolActive = false
        }
      })
    },
    handleCreateClan(param) {
      this.createClan({
        name: param,
      }).then((clan) => {
        if (clan) {
          this.fetchMyClan()
          this.isCreatePoolActive = false
        }
      })
    },
  },
}
</script>

<style module lang="scss">
  @import "../../assets/scss/variables";
  .wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  .userNameText {
    font-weight: 600;
  }
  .scoreText {
    color: $color-tomato;
    font-weight: 600;
  }
  .centeredWrapper {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
  .memberCountText {
    font-size: $font-size-tiny;
    display: block;
  }
  .headerWrapper {
    border-bottom: 2px solid $color-bleached-silk;
  }
  .inviteWrapper {
    border-bottom: 2px solid $color-bleached-silk;
    border-top: 2px solid $color-bleached-silk;
  }
  .copyText {
    width: 100px;
    display: inline-block;
  }
  .joinWarning {
    color: $color-tomato;
    font-size: $font-size-small;
  }
  .isMyPosition {
    transition: all .5s ease-in-out;
    background: lighten($color-alley, 15);
    &:hover {
      background: lighten($color-alley, 9);
    }
  }
  .positionWrapper {
    transition: all .5s ease-in-out;
    &:hover {
      background: lighten($color-alley, 15);
    }
  }
</style>
